import { useState } from "react";
import { Modal } from "../components/Modal.js";
import { Icon } from "../components/Icon.js";
import { coloringApi } from "./api.js";
import { useColoring } from "./ColoringContext.js";

type StepState = "pending" | "running" | "done" | "skipped" | "error";

interface Step {
  key: "brief" | "style" | "characters" | "plan";
  label: string;
}

const STEPS: Step[] = [
  { key: "brief", label: "Develop the concept into a creative brief" },
  { key: "style", label: "Write the Style Bible" },
  { key: "characters", label: "Design recurring characters (if the book needs them)" },
  { key: "plan", label: "Plan the pages" }
];

/**
 * "Create My Coloring Book": runs the text-AI steps in order, one request each so
 * progress is visible and a failure can be retried from that step. It deliberately
 * stops before any image generation — nothing here spends image credits.
 */
export function CreateBookModal({ onClose }: { onClose: () => void }) {
  const { projectId, state, reload, goTo } = useColoring();
  const [phase, setPhase] = useState<"confirm" | "running" | "done">("confirm");
  const [states, setStates] = useState<Record<string, StepState>>({});
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ pages: number; characters: number } | null>(null);

  const hasWork = !!(state.book.brief && state.book.brief.trim()) || state.pages.length > 0 || state.characters.length > 0;

  const run = async (from = 0) => {
    setPhase("running");
    setError(null);
    for (let i = from; i < STEPS.length; i++) {
      const step = STEPS[i]!;
      setStates((s) => ({ ...s, [step.key]: "running" }));
      try {
        if (step.key === "brief") await coloringApi.developConcept(projectId);
        else if (step.key === "style") await coloringApi.generateStyleBible(projectId);
        else if (step.key === "characters") {
          const current = await coloringApi.state(projectId);
          if (current.characters.length > 0) {
            setStates((s) => ({ ...s, [step.key]: "skipped" }));
            continue;
          }
          await coloringApi.suggestCharacters(projectId);
        } else await coloringApi.planBook(projectId, { pageCount: state.book.pageCount });
        setStates((s) => ({ ...s, [step.key]: "done" }));
      } catch (err) {
        setStates((s) => ({ ...s, [step.key]: "error" }));
        setError(err instanceof Error ? err.message : "That step failed.");
        await reload();
        return;
      }
    }
    const final = await coloringApi.state(projectId);
    await reload();
    setSummary({ pages: final.pages.length, characters: final.characters.length });
    setPhase("done");
  };

  const failedIndex = STEPS.findIndex((s) => states[s.key] === "error");

  return (
    <Modal
      title="Create My Coloring Book"
      subtitle={phase === "done" ? "Your plan is ready" : "Concept, style, characters and a page plan — for you to review"}
      onClose={phase === "running" ? () => undefined : onClose}
      width={560}
      footer={
        phase === "confirm" ? (
          <>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => void run(0)}>
              <Icon name="sparkles" size={15} /> Start
            </button>
          </>
        ) : phase === "done" ? (
          <>
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
            <button
              className="btn btn-primary"
              onClick={() => {
                onClose();
                goTo("pages");
              }}
            >
              Review the pages
            </button>
          </>
        ) : failedIndex >= 0 ? (
          <>
            <button className="btn btn-ghost" onClick={onClose}>Close</button>
            <button className="btn btn-primary" onClick={() => void run(failedIndex)}>Retry this step</button>
          </>
        ) : (
          <span className="faint" style={{ fontSize: 12 }}>Working…</span>
        )
      }
    >
      {phase === "confirm" && (
        <>
          <p style={{ marginTop: 0 }}>
            Scribe will turn your concept into a creative brief, a Style Bible, characters (only if the book needs them) and a page-by-page plan.
          </p>
          {hasWork && (
            <p className="muted" style={{ fontSize: 13 }}>
              <Icon name="info" size={13} /> This rewrites your creative brief and Style Bible. Characters are only added if you have none, and the plan only adds missing pages —
              existing pages are never replaced.
            </p>
          )}
          <p className="faint" style={{ fontSize: 12.5, marginBottom: 0 }}>
            No images are generated and no image credits are spent. You review everything first, then choose what to generate.
          </p>
        </>
      )}
      {phase !== "confirm" && (
        <ul className="create-steps">
          {STEPS.map((s) => {
            const st = states[s.key] ?? "pending";
            return (
              <li key={s.key} className={`create-step ${st}`}>
                <span className="create-step-icon">
                  {st === "running" ? <Icon name="clock" size={14} className="spin" /> : st === "done" || st === "skipped" ? <Icon name="check" size={13} strokeWidth={2.5} /> : st === "error" ? <Icon name="alert" size={14} /> : null}
                </span>
                <span>
                  {s.label}
                  {st === "skipped" && <span className="faint"> — already have characters</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="create-error">{error}</p>}
      {phase === "done" && summary && (
        <p className="strong" style={{ marginBottom: 0 }}>
          {summary.pages} page{summary.pages === 1 ? "" : "s"} planned{summary.characters > 0 ? ` with ${summary.characters} recurring character${summary.characters === 1 ? "" : "s"}` : ""}.
        </p>
      )}
    </Modal>
  );
}
